import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import {
  Badge,
  Card,
  Empty,
  Failure,
  Field,
  Loading,
  Modal,
  Notice,
  Pagination,
} from '../components/ui.js';
import { READINESS, formatRelative } from '../lib/format.js';

const BELL_PROFILE = 'esp32-dfplayer-bell';

interface Device {
  id: string;
  name: string;
  serial: string;
  status: string;
  board_profile: string;
  firmware_version: string | null;
  school: { id: string; name: string; timezone: string };
  last_seen_at: string | null;
  desired_revision: number;
  applied_revision: number;
  readiness: string;
  online: boolean;
  reasons: string[];
}

interface Page<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export function DevicesPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [readiness, setReadiness] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [enrollment, setEnrollment] = useState<{
    token: string;
    expiresAt: string;
    profile: string;
    serial: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schoolId = params.get('school_id') ?? '';

  const devices = useQuery({
    queryKey: ['devices', page, search, schoolId, readiness],
    queryFn: () =>
      get<Page<Device>>(
        `/devices${qs({ page, page_size: 25, q: search, school_id: schoolId, readiness })}`,
      ),
    refetchInterval: 20_000,
  });

  const schools = useQuery({
    queryKey: ['schools', 'all'],
    queryFn: () => get<Page<{ id: string; name: string }>>('/schools?page_size=200'),
  });

  const createDevice = useMutation({
    mutationFn: (body: Record<string, string>) => post('/devices', body),
    onSuccess: () => {
      setShowForm(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const issueToken = useMutation({
    mutationFn: (device: Device) =>
      post<{ enrollment_token: string; expires_at: string }>(`/devices/${device.id}/enrollment`),
    onSuccess: (data, device) =>
      setEnrollment({
        token: data.enrollment_token,
        expiresAt: data.expires_at,
        profile: device.board_profile,
        serial: device.serial,
      }),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card
        title="الأجهزة"
        subtitle={`${devices.data?.total ?? 0} جهاز`}
        actions={
          <>
            <input
              placeholder="بحث بالاسم أو الرقم التسلسلي"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ width: 220 }}
            />
            <select
              aria-label="تصفية بالمدرسة"
              value={schoolId}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                if (e.target.value) next.set('school_id', e.target.value);
                else next.delete('school_id');
                setParams(next);
                setPage(1);
              }}
              style={{ width: 'auto' }}
            >
              <option value="">كل المدارس</option>
              {schools.data?.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              aria-label="تصفية بالجاهزية"
              value={readiness}
              onChange={(e) => {
                setReadiness(e.target.value);
                setPage(1);
              }}
              style={{ width: 'auto' }}
            >
              <option value="">كل الحالات</option>
              <option value="ready">جاهز</option>
              <option value="degraded">يعمل مع ملاحظات</option>
              <option value="not_ready">غير جاهز</option>
              <option value="unknown">غير معروف</option>
            </select>
            {can('device.manage') ? (
              <button className="primary" onClick={() => setShowForm(true)}>
                إضافة جهاز
              </button>
            ) : null}
          </>
        }
      >
        <Notice tone="info">
          الجاهزية ليست مجرد اتصال: تشمل وجود جدول صالح وصوت متاح ووقت موثوق وعدم وجود عطل حرج.
        </Notice>

        {devices.isLoading ? (
          <Loading />
        ) : devices.error ? (
          <Failure error={devices.error} onRetry={() => void devices.refetch()} />
        ) : devices.data!.items.length === 0 ? (
          <Empty title="لا توجد أجهزة" hint="أضف الجهاز هنا أولًا ثم سجّله عبر USB برمز تسجيل." />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الجهاز</th>
                    <th>المدرسة</th>
                    <th>الجاهزية</th>
                    <th>الاتصال</th>
                    <th>نسخة الجدول</th>
                    <th>الإصدار</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {devices.data!.items.map((d) => {
                    const r = READINESS[d.readiness] ?? READINESS.unknown!;
                    const behind = d.applied_revision < d.desired_revision;
                    return (
                      <tr key={d.id}>
                        <td>
                          <Link to={`/devices/${d.id}`}>{d.name}</Link>
                          <div className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                            {d.serial}
                          </div>
                          {d.board_profile === BELL_PROFILE ? (
                            <div style={{ fontSize: 12, color: 'var(--muted)' }}>جرس رقمي ESP32 + MP3</div>
                          ) : null}
                        </td>
                        <td>{d.school.name}</td>
                        <td>
                          <Badge tone={r.tone} icon={r.icon}>
                            {r.label}
                          </Badge>
                          {d.status !== 'ACTIVE' ? (
                            <div style={{ marginTop: 4 }}>
                              <Badge tone="muted" icon="…">
                                {d.status === 'REVOKED' ? 'ملغى' : 'لم يُسجَّل'}
                              </Badge>
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {d.online ? (
                            <Badge tone="ok" icon="✔">
                              متصل
                            </Badge>
                          ) : (
                            <Badge tone="warn" icon="▲">
                              منقطع
                            </Badge>
                          )}
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                            {formatRelative(d.last_seen_at)}
                          </div>
                        </td>
                        <td>
                          <span className="num">{d.applied_revision}</span>
                          {behind ? (
                            <>
                              {' / '}
                              <span className="num" style={{ color: 'var(--warn)' }}>
                                {d.desired_revision}
                              </span>
                            </>
                          ) : null}
                        </td>
                        <td className="mono" style={{ fontSize: 12.5 }}>
                          {d.firmware_version ?? '—'}
                        </td>
                        <td className="actions">
                          {can('device.enroll') && d.status !== 'REVOKED' ? (
                            <button
                              className="ghost"
                              disabled={issueToken.isPending}
                              onClick={() => issueToken.mutate(d)}
                            >
                              رمز تسجيل
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={devices.data!.page}
              totalPages={devices.data!.total_pages}
              total={devices.data!.total}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      {showForm ? (
        <DeviceForm
          schools={schools.data?.items ?? []}
          busy={createDevice.isPending}
          onCancel={() => setShowForm(false)}
          onSubmit={(body) => createDevice.mutate(body)}
        />
      ) : null}

      {enrollment ? (
        <Modal
          title="رمز التسجيل"
          onClose={() => setEnrollment(null)}
          footer={<button onClick={() => setEnrollment(null)}>إغلاق</button>}
        >
          <Notice tone="warn">
            يظهر هذا الرمز مرة واحدة فقط، وصالح لعشر دقائق ولاستعمال واحد.
            {enrollment.profile === BELL_PROFILE
              ? ' صِل الجهاز بالحاسوب عبر USB وافتح Serial Monitor بسرعة 115200، ثم الصق السطر التالي كما هو واضغط Enter.'
              : ' أدخله في أداة التسجيل عبر USB ولا ترسله في رسالة غير آمنة.'}
          </Notice>
          <div
            className="mono"
            style={{
              padding: 12,
              background: 'var(--surface-2)',
              borderRadius: 8,
              wordBreak: 'break-all',
              userSelect: 'all',
            }}
          >
            {enrollment.profile === BELL_PROFILE ? `enroll ${enrollment.token}` : enrollment.token}
          </div>
          {enrollment.profile === BELL_PROFILE ? (
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>
              يجب أن يطبع الجهاز عند الإقلاع الرقم التسلسلي نفسه المسجل هنا:{' '}
              <span className="mono">{enrollment.serial}</span>. بعد نجاح التسجيل يضيء المصباح الأزرق
              ثابتًا ويظهر الجهاز «متصل».
            </p>
          ) : null}
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>
            ينتهي في: <span className="mono">{new Date(enrollment.expiresAt).toISOString()}</span>
          </p>
        </Modal>
      ) : null}
    </>
  );
}

function DeviceForm({
  schools,
  busy,
  onCancel,
  onSubmit,
}: {
  schools: Array<{ id: string; name: string }>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, string>) => void;
}): JSX.Element {
  const [form, setForm] = useState({
    school_id: schools[0]?.id ?? '',
    name: '',
    serial: '',
    board_profile: BELL_PROFILE,
  });

  return (
    <Modal
      title="إضافة جهاز"
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button
            className="primary"
            disabled={busy || !form.school_id || !form.name || form.serial.length < 4}
            onClick={() =>
              onSubmit({
                school_id: form.school_id,
                name: form.name.trim(),
                serial: form.serial.trim(),
                board_profile: form.board_profile,
              })
            }
          >
            {busy ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
        </>
      }
    >
      <Field label="المدرسة">
        <select value={form.school_id} onChange={(e) => setForm((f) => ({ ...f, school_id: e.target.value }))}>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="اسم الجهاز" hint="مثل: جهاز الساحة، جهاز الطابق الأول">
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </Field>
      <Field label="نوع اللوحة">
        <select
          value={form.board_profile}
          onChange={(e) => setForm((f) => ({ ...f, board_profile: e.target.value }))}
        >
          <option value={BELL_PROFILE}>جرس رقمي ESP32 + MP3 (DFPlayer)</option>
          <option value="esp32s3-devkitc1-n8r8">ESP32-S3-DevKitC-1 (N8R8)</option>
        </select>
      </Field>
      <Field
        label="الرقم التسلسلي"
        hint={
          form.board_profile === BELL_PROFILE
            ? 'يطبعه الجهاز في Serial Monitor عند الإقلاع بالشكل BELL-XXXXXXXXXXXX. انسخه كما هو.'
            : 'يُثبَّت على الصندوق. يُستخدم للمطابقة عند التسجيل، وليس ككلمة مرور.'
        }
      >
        <input
          className="mono"
          value={form.serial}
          onChange={(e) => setForm((f) => ({ ...f, serial: e.target.value }))}
        />
      </Field>
    </Modal>
  );
}
